import React, { useCallback, useMemo, useState } from "react";
import { MermaidScrollGuard } from "@/components/molecules/MermaidScrollGuard";
import { Streamdown } from "streamdown";
import { mermaid } from "@streamdown/mermaid";
import { code } from "@streamdown/code";
import "streamdown/styles.css";
import "./streamdownOverrides.css";
import { fixMermaidUnquotedLabels } from "../dev-agent/components/mermaidScrub";
import { DocInfoBanner } from "@/components/molecules/docs/DocInfoBanner";
import {
  Button,
  FileExplorer as UIFileExplorer,
  type FileNode,
} from "@ncoder/ui";
import {
  Loader2,
  Code2,
  FileText,
  AlertCircle,
  RefreshCw,
  Folder,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import Editor from "@monaco-editor/react";
import { useMetadataTypes, useMetadataItem } from "@/hooks/metadata_v1";
import { createMetadataLoadChildren } from "@/hooks/metadata_v1/createMetadataLoadChildren";
import { useAuthStoreV1 } from "@/stores/authStore_v1";
import { useGenerateDocumentation } from "@/hooks/documentation";
import { QuickOpen } from "@/components/organisms/metadata_v1";

const DOCS_PLUGINS = { mermaid, code };

// Monaco editor language per metadata type. Docs are generated for ANY type, so
// the Code tab needs to highlight Apex, XML metadata, and JS bundles sensibly.
// Anything unmapped falls back to XML (the dominant metadata serialization).
const APEX_TYPES = new Set(["ApexClass", "ApexTrigger"]);
const JS_BUNDLE_TYPES = new Set(["LightningComponentBundle", "AuraDefinitionBundle"]);

function monacoLanguageFor(metadataType: string, fileName?: string): string {
  if (APEX_TYPES.has(metadataType)) return "apex";
  if (JS_BUNDLE_TYPES.has(metadataType)) {
    const fn = (fileName ?? "").toLowerCase();
    if (fn.endsWith(".html") || fn.endsWith(".cmp")) return "html";
    if (fn.endsWith(".css")) return "css";
    return "javascript";
  }
  return "xml";
}

// Primary types — the ones with dedicated parsers + bulk doc-ingest. These are
// shown at the top level of the sidebar; every other metadata type is hidden
// behind a "Show other types" toggle (still fully documentable on click).
const PRIMARY_DOC_TYPES = new Set([
  "ApexClass",
  "ApexTrigger",
  "Flow",
  "FlowDefinition",
  "CustomObject",
  "LightningComponentBundle",
]);

type SelectedFile = {
  id: string;
  fullName: string;
  metadataType: string;
};

// Path produced by createMetadataLoadChildren is `/{metadataType}/{fullName}`.
// Recover fullName so the doc-generation API can address the file by name.
function fileNodeToSelectedFile(file: FileNode): SelectedFile | null {
  if (file.type !== "file" || !file.metadataType) return null;
  const prefix = `/${file.metadataType}/`;
  const fullName = file.path.startsWith(prefix)
    ? file.path.slice(prefix.length)
    : file.name;
  return {
    id: file.id,
    fullName,
    metadataType: file.metadataType,
  };
}

export const Documentation: React.FC = () => {
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [selectedFile, setSelectedFile] = useState<SelectedFile | null>(null);
  const [selectedFileNode, setSelectedFileNode] = useState<FileNode | null>(null);
  const [activeTab, setActiveTab] = useState<"code" | "docs">("docs");
  const [isQuickOpenOpen, setIsQuickOpenOpen] = useState(false);
  const [showOtherTypes, setShowOtherTypes] = useState(false);

  const isMac =
    typeof navigator !== "undefined" && navigator.platform.toLowerCase().includes("mac");
  const searchShortcutLabel = isMac ? "⌘P" : "Ctrl+P";

  const selectedOrg = useAuthStoreV1((state) => state.selectedOrg);
  const retrievalId = selectedOrg?.latestMetadataRetrieval?.id ?? null;

  const { data: allMetadataTypes = [], isLoading: isLoadingTypes } = useMetadataTypes();

  // Show every metadata type in the org. Docs generate on the fly for any type
  // (dedicated parsers for Apex/Flow/CustomObject/LWC, a generic LLM doc for the
  // rest), so there's no longer an allow-list. Sort alphabetically for browsing.
  const metadataTypes = useMemo(
    () =>
      [...allMetadataTypes].sort((a, b) =>
        a.metadataType.localeCompare(b.metadataType)
      ),
    [allMetadataTypes]
  );

  const totalDocumentationComponents = useMemo(
    () => metadataTypes.reduce((sum, type) => sum + type.count, 0),
    [metadataTypes]
  );

  // Split the type folders into primary (ingested, always shown) and "other"
  // (revealed via the toggle). `allFiles` always holds every type so QuickOpen
  // search spans all of them regardless of the toggle.
  const { primaryFiles, otherFiles } = useMemo(() => {
    const primary: FileNode[] = [];
    const other: FileNode[] = [];
    for (const type of metadataTypes) {
      const node: FileNode = {
        id: type.metadataType,
        name: type.metadataType,
        type: "folder" as const,
        path: `/${type.metadataType}`,
        metadataType: type.metadataType,
        count: type.count,
        children: [],
      };
      (PRIMARY_DOC_TYPES.has(type.metadataType) ? primary : other).push(node);
    }
    return { primaryFiles: primary, otherFiles: other };
  }, [metadataTypes]);

  const allFiles = useMemo<FileNode[]>(
    () => [...primaryFiles, ...otherFiles],
    [primaryFiles, otherFiles]
  );

  const visibleFiles = useMemo<FileNode[]>(
    () => (showOtherTypes ? allFiles : primaryFiles),
    [showOtherTypes, allFiles, primaryFiles]
  );

  const loadChildren = useMemo(
    () => createMetadataLoadChildren({ retrievalId }),
    [retrievalId]
  );

  const handleToggleFolder = useCallback((folderId: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  }, []);

  const { data: fileCode, isLoading: isLoadingCode } = useMetadataItem(selectedFile?.id);

  const {
    generateDocs,
    isPending: isGeneratingDocs,
    data: generatedDocs,
    error: docsError,
    reset: resetDocs,
  } = useGenerateDocumentation();

  const handleFileSelect = useCallback(
    (file: FileNode) => {
      const mapped = fileNodeToSelectedFile(file);
      if (!mapped) return;
      setSelectedFile(mapped);
      setSelectedFileNode(file);
      setActiveTab("docs");
      resetDocs();
      generateDocs({
        file_id: mapped.id,
        class_name: mapped.fullName,
        metadata_type: mapped.metadataType,
        enrich: true,
      }).catch(() => {});
    },
    [generateDocs, resetDocs]
  );

  const handleDocsTabClick = useCallback(() => {
    if (!selectedFile) return;
    setActiveTab("docs");
    if (!generatedDocs && !isGeneratingDocs) {
      generateDocs({
        file_id: selectedFile.id,
        class_name: selectedFile.fullName,
        metadata_type: selectedFile.metadataType,
        enrich: true,
      }).catch(() => {});
    }
  }, [selectedFile, generatedDocs, isGeneratingDocs, generateDocs]);

  const docsContent: string | null = generatedDocs?.markdown?.trim() || null;

  return (
    <div className="flex h-full min-h-0 w-full flex-col gap-4">
      <DocInfoBanner />

      <div className="flex min-h-0 flex-1 gap-4 overflow-hidden">
        <div className="flex h-full w-56 shrink-0 flex-col overflow-hidden rounded-lg border border-border bg-card md:w-72">
          <div className="min-h-0 flex-1 overflow-hidden">
            <UIFileExplorer
              title="Metadata Types"
              files={visibleFiles}
              isLoading={isLoadingTypes}
              expandedFolders={expandedFolders}
              onToggleFolder={handleToggleFolder}
              selectedFile={selectedFileNode}
              onFileSelect={handleFileSelect}
              loadChildren={loadChildren}
              itemCount={
                !isLoadingTypes && visibleFiles.length > 0 ? visibleFiles.length : undefined
              }
              search={{
                onClick: () => setIsQuickOpenOpen(true),
                placeholder: "Search files...",
                shortcutLabel: searchShortcutLabel,
              }}
            />
          </div>
          {!isLoadingTypes && otherFiles.length > 0 && (
            <button
              type="button"
              onClick={() => setShowOtherTypes((v) => !v)}
              className="flex shrink-0 items-center justify-center gap-1.5 border-t border-border bg-muted/30 px-3 py-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              {showOtherTypes ? (
                <>
                  <ChevronUp className="h-3.5 w-3.5" />
                  Hide other types
                </>
              ) : (
                <>
                  <ChevronDown className="h-3.5 w-3.5" />
                  Show {otherFiles.length} other type{otherFiles.length === 1 ? "" : "s"}
                </>
              )}
            </button>
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-card">
          {selectedFile ? (
            <div className="flex h-full flex-col">
              <div className="flex items-center justify-between border-b border-border px-6 py-4">
                <div>
                  <h2 className="text-xl font-bold">{selectedFile.fullName}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {selectedFile.metadataType}
                  </p>
                </div>

                <div className="flex items-center gap-1 rounded-lg border border-border bg-muted/50 p-1">
                  <Button
                    variant={activeTab === "docs" ? "default" : "ghost"}
                    size="sm"
                    onClick={handleDocsTabClick}
                    className="gap-2"
                  >
                    <FileText className="h-4 w-4" />
                    Docs
                  </Button>
                  <Button
                    variant={activeTab === "code" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setActiveTab("code")}
                    className="gap-2"
                  >
                    <Code2 className="h-4 w-4" />
                    Code
                  </Button>
                </div>
              </div>

              <div className="flex-1 min-h-0 overflow-hidden">
                {activeTab === "code" && (
                  <div className="h-full">
                    {isLoadingCode ? (
                      <div className="flex h-full items-center justify-center">
                        <div className="flex items-center gap-2">
                          <Loader2 className="h-5 w-5 animate-spin" />
                          <span className="text-sm text-muted-foreground">
                            Loading code...
                          </span>
                        </div>
                      </div>
                    ) : fileCode ? (
                      <Editor
                        height="100%"
                        language={monacoLanguageFor(
                          selectedFile.metadataType,
                          fileCode.fileName
                        )}
                        value={fileCode.content}
                        options={{
                          readOnly: true,
                          minimap: { enabled: true },
                          fontSize: 14,
                          automaticLayout: true,
                          wordWrap: "on",
                          scrollBeyondLastLine: false,
                          glyphMargin: true,
                          lineNumbers: "on",
                          renderLineHighlight: "all",
                          showFoldingControls: "always",
                        }}
                        theme="vs-dark"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center">
                        <p className="text-sm text-muted-foreground">
                          No code available
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === "docs" && (
                  <div className="h-full overflow-auto">
                    {isGeneratingDocs && (
                      <div className="flex h-full items-center justify-center">
                        <div className="flex flex-col items-center gap-3 text-center">
                          <Loader2 className="h-8 w-8 animate-spin text-primary" />
                          <p className="text-sm font-medium text-foreground">Generating documentation…</p>
                          <p className="text-xs text-muted-foreground">
                            AI is analysing <span className="font-semibold">{selectedFile.fullName}</span>
                          </p>
                        </div>
                      </div>
                    )}

                    {!isGeneratingDocs && docsError && (
                      <div className="flex h-full flex-col items-center justify-center gap-4 p-6">
                        <AlertCircle className="h-10 w-10 text-destructive" />
                        <p className="text-sm font-medium text-foreground">Failed to generate documentation</p>
                        <p className="max-w-sm text-center text-xs text-muted-foreground">
                          {docsError.message}
                        </p>
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-2"
                          onClick={() => {
                            resetDocs();
                            generateDocs({
                              file_id: selectedFile.id,
                              class_name: selectedFile.fullName,
                              metadata_type: selectedFile.metadataType,
                              enrich: true,
                            }).catch(() => {});
                          }}
                        >
                          <RefreshCw className="h-4 w-4" />
                          Retry
                        </Button>
                      </div>
                    )}

                    {!isGeneratingDocs && !docsError && docsContent && (
                      <div className="h-full overflow-auto p-6">
                        <article className="docs-streamdown prose prose-sm dark:prose-invert max-w-none">
                          <MermaidScrollGuard>
                            <Streamdown
                              plugins={DOCS_PLUGINS}
                              controls={{ mermaid: false }}
                            >
                              {fixMermaidUnquotedLabels(docsContent)}
                            </Streamdown>
                          </MermaidScrollGuard>
                        </article>
                      </div>
                    )}

                    {!isGeneratingDocs && !docsError && !docsContent && (
                      <div className="flex h-full items-center justify-center p-6">
                        <div className="text-center">
                          <FileText className="mx-auto h-12 w-12 text-muted-foreground/50" />
                          <p className="mt-4 text-sm font-medium text-foreground">
                            No documentation yet
                          </p>
                          <p className="mt-2 text-sm text-muted-foreground max-w-md">
                            Click the Docs tab again or wait a moment while the AI generates documentation.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="overflow-auto p-6">
              <div className="mx-auto w-full max-w-4xl">
                <h2 className="text-xl font-bold">Welcome to Documentation</h2>
                <p className="mt-4 text-muted-foreground">
                  Select a metadata type from the left panel to explore files and
                  view their code or documentation.
                </p>

                <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-2">
                  <div className="rounded-lg border border-border bg-muted/30 p-4">
                    <div className="text-2xl font-bold">
                      {metadataTypes.length}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Metadata Types
                    </div>
                  </div>
                  <div className="rounded-lg border border-border bg-muted/30 p-4">
                    <div className="text-2xl font-bold">
                      {totalDocumentationComponents}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Total Components
                    </div>
                  </div>
                </div>

                <div className="mt-8 space-y-6">
                  <div>
                    <h2 className="mb-3 text-lg font-semibold">
                      Getting Started
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Click on any metadata type in the left panel to view its
                      components. Then select a component to view its code or
                      documentation.
                    </p>
                  </div>

                  <div>
                    <h2 className="mb-3 text-lg font-semibold">Features</h2>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-center gap-2">
                        <Code2 className="h-4 w-4 text-green-500" />
                        View source code with syntax highlighting
                      </li>
                      <li className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-blue-500" />
                        Read AI-generated documentation
                      </li>
                      <li className="flex items-center gap-2">
                        <Folder className="h-4 w-4 text-amber-500" />
                        Browse by metadata type
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <QuickOpen
        files={allFiles}
        isOpen={isQuickOpenOpen}
        onOpenChange={setIsQuickOpenOpen}
        onFileSelect={handleFileSelect}
      />
    </div>
  );
};

export default Documentation;
